import React from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';
import { Area, AreaChart, ResponsiveContainer, Tooltip, YAxis } from 'recharts';

import {
  BG_1,
  BORDER_2,
  ERROR_BG,
  ERROR_TEXT,
  FG_1,
  FG_3,
  PRIMARY_COLOR,
  PRIMARY_LIGHT,
  RADIUS_MD,
  SHADOW_SM,
  SUCCESS_BG,
  SUCCESS_TEXT,
} from 'components/upb_viewer_beta/css/betaTokens';
import { fmtDate, formatByType } from 'components/upb_match_explorer/format';

function TrendTooltip({ active, payload, metricType, label }) {
  if (!active || !payload || !payload.length) {
    return null;
  }
  const point = payload[0].payload;
  return (
    <div className={css(styles.tooltip)}>
      <div className={css(styles.tooltipName)}>{label}</div>
      <div className={css(styles.tooltipRow)}>
        <span>{fmtDate(point.date)}</span>
        <b>{formatByType(metricType, point.value)}</b>
      </div>
    </div>
  );
}

TrendTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.object),
  metricType: PropTypes.string,
  label: PropTypes.string,
};

function MetricTrendCard({ metric, series }) {
  const points = series || [];
  const hasSeries = points.length > 0;
  const current = hasSeries ? points[points.length - 1].value : null;
  const first = hasSeries ? points[0].value : null;

  let deltaClass = styles.deltaFlat;
  let deltaText = '±0.0%';
  if (hasSeries && first) {
    const pct = ((current - first) / first) * 100;
    if (Math.abs(pct) >= 0.05) {
      deltaClass = pct > 0 ? styles.deltaUp : styles.deltaDown;
      deltaText = `${pct > 0 ? '▲' : '▼'} ${Math.abs(pct).toFixed(1)}%`;
    }
  }

  const gradientId = `spark-${metric.key}`;

  return (
    <div className={css(styles.card, metric.feature && styles.feature)}>
      <div className={css(styles.label)}>{metric.label}</div>
      <div className={css(styles.valueRow)}>
        <span className={css(styles.value)}>{formatByType(metric.type, current)}</span>
        <span className={css(styles.delta, deltaClass)}>{deltaText}</span>
      </div>
      <div className={css(styles.spark)}>
        {points.length > 1 ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={points} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={PRIMARY_COLOR} stopOpacity={0.28} />
                  <stop offset="100%" stopColor={PRIMARY_COLOR} stopOpacity={0} />
                </linearGradient>
              </defs>
              <YAxis hide domain={['dataMin', 'dataMax']} />
              <Tooltip
                cursor={{ stroke: PRIMARY_COLOR, strokeWidth: 1 }}
                content={<TrendTooltip metricType={metric.type} label={metric.label} />}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke={PRIMARY_COLOR}
                strokeWidth={2}
                fill={`url(#${gradientId})`}
                dot={false}
                activeDot={{ r: 3 }}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className={css(styles.noSeries)}>Not enough history</div>
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  card: {
    background: BG_1,
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_MD,
    boxShadow: SHADOW_SM,
    padding: '15px 16px',
    overflow: 'hidden',
  },
  feature: {
    gridColumn: 'span 2',
    background: `linear-gradient(135deg, ${PRIMARY_LIGHT}, transparent)`,
  },
  label: { fontSize: 12, color: FG_3, fontWeight: 600 },
  valueRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 8,
    marginTop: 8,
  },
  value: { fontSize: 24, fontWeight: 700, color: FG_1, fontVariantNumeric: 'tabular-nums' },
  delta: {
    fontSize: 11,
    fontWeight: 700,
    padding: '2px 7px',
    borderRadius: 20,
    whiteSpace: 'nowrap',
  },
  deltaUp: { backgroundColor: SUCCESS_BG, color: SUCCESS_TEXT },
  deltaDown: { backgroundColor: ERROR_BG, color: ERROR_TEXT },
  deltaFlat: { backgroundColor: BORDER_2, color: FG_3 },
  spark: { height: 46, marginTop: 12 },
  noSeries: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    fontSize: 11.5,
    color: FG_3,
  },
  tooltip: {
    background: FG_1,
    color: BG_1,
    padding: '8px 11px',
    borderRadius: 9,
    fontSize: 12,
    minWidth: 130,
  },
  tooltipName: { fontWeight: 700, marginBottom: 3 },
  tooltipRow: { display: 'flex', justifyContent: 'space-between', gap: 16, opacity: 0.9 },
});

MetricTrendCard.propTypes = {
  metric: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    feature: PropTypes.bool,
  }).isRequired,
  series: PropTypes.arrayOf(PropTypes.shape({ date: PropTypes.string, value: PropTypes.number })),
};

export default MetricTrendCard;
